import { AxiosHeaders } from 'axios';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useAuth } from '@/state/auth';
import { UseExtendQueryOptions, useQuery } from '@/utils/api';
import { isArrayBufferEmpty } from '@/utils/misc.util';

// S3 key for profile pictures in a personal (individual user) tenant
export const PERSONAL_TENANT_PROFILE_PICTURE_ID = '#profilePicture#';

// Semantic alias — use when the context is "a user's personal profile picture"
export const USER_PROFILE_PICTURE_ID = PERSONAL_TENANT_PROFILE_PICTURE_ID;

// S3 key for pictures in an organization/customer tenant
export const ORGANIZATION_TENANT_PROFILE_PICTURE_ID =
  '#organizationProfilePicture#';

// Display name keys
export const USER_DISPLAY_NAME_KEY = 'display-name';
export const ORGANIZATION_DISPLAY_NAME_KEY = 'organization-display-name';

async function computeSHA256(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
}
interface GetProfilePictureUrl {
  email: string;
  isOrganization?: boolean;
}

export interface ProfilePictureUrl {
  url: string;
  isGavatar: boolean;
}

export function useGetProfilePictureUrl(
  props: GetProfilePictureUrl,
  options?: UseExtendQueryOptions<ProfilePictureUrl | null>
) {
  const { me } = useAuth();

  const axios = useAxios();

  // Determine which key to use based on context
  const profilePictureId = props.isOrganization
    ? ORGANIZATION_TENANT_PROFILE_PICTURE_ID
    : PERSONAL_TENANT_PROFILE_PICTURE_ID;

  return useQuery<ProfilePictureUrl | null>({
    ...options,
    defaultErrorMessage: 'Failed to fetch profile picture',
    queryKey: [
      ...getQueryKey.getGavatarProfilePicture(props.email),
      props.isOrganization ? 'organization' : 'personal',
    ],
    queryFn: async () => {
      try {
        // Step 1: Get presigned URL from backend
        const res = await axios.get<{ url: string }>(`/file`, {
          params: {
            name: profilePictureId,
          },
          headers: {
            common: {
              account: props.email === me ? undefined : props.email,
            },
          } as unknown as AxiosHeaders,
        });

        const presignedUrl = res.data.url;

        if (!presignedUrl) {
          throw new Error('No presigned URL returned');
        }

        // Step 2: Fetch file data from presigned URL
        const fileResponse = await fetch(presignedUrl);

        if (!fileResponse.ok) {
          throw new Error('Failed to fetch profile picture from presigned URL');
        }

        const arrayBuffer = await fileResponse.arrayBuffer();

        if (!isArrayBufferEmpty(arrayBuffer)) {
          const url = URL.createObjectURL(
            new Blob([arrayBuffer], { type: 'image/jpeg' })
          );

          return { url, isGavatar: false };
        }
        // If HEAD fails or content is empty, fall through to fallback
      } catch {
        // ignore
      }

      // Step 2.5: Fallback for organization - try personal profile picture
      if (props.isOrganization) {
        try {
          const fallbackRes = await axios.get<{ url: string }>(`/file`, {
            params: {
              name: PERSONAL_TENANT_PROFILE_PICTURE_ID,
            },
            headers: {
              common: {
                account: props.email === me ? undefined : props.email,
              },
            } as unknown as AxiosHeaders,
          });

          const fallbackPresignedUrl = fallbackRes.data.url;

          if (fallbackPresignedUrl) {
            const fallbackFileResponse = await fetch(fallbackPresignedUrl);

            if (fallbackFileResponse.ok) {
              const fallbackArrayBuffer =
                await fallbackFileResponse.arrayBuffer();

              if (!isArrayBufferEmpty(fallbackArrayBuffer)) {
                const url = URL.createObjectURL(
                  new Blob([fallbackArrayBuffer], { type: 'image/jpeg' })
                );

                return { url, isGavatar: false };
              }
            }
          }
        } catch {
          // Fallback failed, continue to Gravatar
        }
      }

      // Step 3: Check if Gravatar exists with HEAD request (no console errors)
      const hash = await computeSHA256(props.email.trim().toLowerCase());
      const gravatarUrl = `https://www.gravatar.com/avatar/${hash}?d=404`;

      try {
        const headResponse = await fetch(gravatarUrl, { method: 'HEAD' });

        if (headResponse.ok) {
          return {
            url: gravatarUrl,
            isGavatar: true,
          };
        }
      } catch {
        // Gravatar doesn't exist, fall through to return null
      }

      return null;
    },
  });
}
